import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Grid, Stack, Typography, alpha } from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@mui/icons-material';
import SLUGS from '../../resources/slugs';
import {
  TrendingUp as UpIcon,
  TrendingDown as DownIcon,
  CheckCircle as OnTimeIcon,
  WatchLater as LateIcon,
  Timelapse as HoursIcon,
  MoreTime as OvertimeIcon,
} from '@mui/icons-material';
import {
  Area, AreaChart, Bar, BarChart, Cell, Pie, PieChart, ResponsiveContainer,
  Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts';

/* ------------------------------------------------------------------ *
 * Metric helpers — derive On-Time %, Late %, working & overtime hours
 * from the per-day attendance payload (data.days). Shared by the
 * employee dashboard and the admin attendance calendar.
 * ------------------------------------------------------------------ */

const durToMinutes = (raw) => {
  if (!raw) return 0;
  const parts = String(raw).split(':').map((x) => parseInt(x, 10) || 0);
  if (parts.length >= 3) return parts[0] * 60 + parts[1] + parts[2] / 60;
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return 0;
};

const parseHHMM = (s) => {
  if (!s) return null;
  const [h, m] = String(s).split(':').map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
};

// Mirrors backend `_late_by_delta`. The standard day shift starts 08:00 (on
// time by 08:15 with the 15-min grace); Saturday is a 10:00-16:00 shift for all
// employees (on time by 10:15). Afternoon/evening punches (after noon) are
// expected by 15:30. `iso` is the day's date (YYYY-MM-DD) for the Saturday rule.
export const isOnTime = (inTime, iso) => {
  const t = parseHHMM(inTime);
  if (t === null) return null;
  let isSaturday = false;
  if (iso) {
    const [yy, mm, dd] = String(iso).split('-').map(Number);
    isSaturday = new Date(yy, mm - 1, dd).getDay() === 6; // local date — tz-safe
  }
  let expected;
  if (t < 12 * 60) {
    expected = isSaturday ? 10 * 60 + 15 : 8 * 60 + 15;
  } else {
    expected = 15 * 60 + 30;
  }
  return t <= expected;
};

export const fmtHoursMinutes = (mins) => {
  const total = Math.max(0, Math.round(mins));
  return { h: Math.floor(total / 60), m: total % 60 };
};

export const buildAttendanceMetrics = (data) => {
  const days = data?.days || {};
  const entries = Object.entries(days).sort(([a], [b]) => (a < b ? -1 : 1));

  let onTime = 0;
  let late = 0;
  let withPunch = 0;
  let workMin = 0;
  let otMin = 0;
  let lateMin = 0;
  const workSeries = [];
  const onTimeSeries = [];
  const lateSeries = [];

  entries.forEach(([iso, cell]) => {
    if (!cell) return;
    if (cell.in_time) {
      withPunch += 1;
      // Prefer the backend's per-day flag — it knows the employee's actual shift
      // (attendance_schema) and the Saturday/Sunday rules. Fall back to the local
      // heuristic only for older responses that lack is_late.
      const ot = (typeof cell.is_late === 'boolean') ? !cell.is_late : isOnTime(cell.in_time, iso);
      if (ot) onTime += 1; else late += 1;
      onTimeSeries.push({ v: ot ? 1 : 0 });
      const dayLateMin = durToMinutes(cell.late_by);
      lateMin += dayLateMin;
      lateSeries.push({ v: Number((dayLateMin / 60).toFixed(2)) });
    }
    const wm = durToMinutes(cell.work_duration);
    workMin += wm;
    if (wm > 0) workSeries.push({ v: Number((wm / 60).toFixed(2)) });
    otMin += durToMinutes(cell.overtime);
  });

  return {
    onTimePct: withPunch ? Math.round((onTime / withPunch) * 100) : 0,
    latePct: withPunch ? Math.round((late / withPunch) * 100) : 0,
    workMin,
    otMin,
    lateMin,
    workSeries,
    onTimeSeries,
    lateSeries,
    withPunch,
  };
};

const pctDelta = (cur, prev) => {
  if (prev === null || prev === undefined) return null;
  if (prev === 0) return cur > 0 ? 100 : 0;
  return Math.round(((cur - prev) / Math.abs(prev)) * 100);
};

/* ------------------------------------------------------------------ */

const Sparkline = ({ data, color, gradientId, compact = false }) => {
  const series = (data && data.length > 1) ? data : [{ v: 0 }, { v: 0 }];
  return (
    <Box sx={{ width: '100%', height: compact ? 34 : 48 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 6, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.45} />
              <stop offset="100%" stopColor={color} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={2.5}
            fill={`url(#${gradientId})`}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </Box>
  );
};

const InsightCard = ({ icon, label, value, sub, color, series, gradientId, delta, goodWhenUp = true, compact = false }) => {
  const hasDelta = delta !== null && delta !== undefined;
  const isUp = hasDelta && delta >= 0;
  const isGood = hasDelta && (goodWhenUp ? isUp : !isUp);
  const deltaColor = isGood ? '#16a34a' : '#dc2626';

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1.25 : 2,
        borderRadius: 3,
        height: compact ? 112 : '100%',
        boxSizing: 'border-box',
        border: '1px solid rgba(229,231,235,0.8)',
        background: 'rgba(255,255,255,0.9)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: compact ? 0 : 0.5 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: 1.5,
          bgcolor: alpha(color, 0.12), color,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, letterSpacing: 0.2 }}>
          {label}
        </Typography>
      </Stack>

      <Grid container alignItems="flex-end" spacing={1}>
        <Grid item xs={compact ? 8 : 7}>
          <Typography variant="h5" fontWeight={800} sx={{ color: '#111827', lineHeight: 1.1, fontSize: compact ? '1.15rem' : undefined, whiteSpace: compact ? 'nowrap' : 'normal' }}>
            {value}
          </Typography>
          {sub && <Typography variant="caption" sx={{ color: '#9ca3af' }}>{sub}</Typography>}
        </Grid>
        <Grid item xs={compact ? 4 : 5}>
          <Sparkline data={series} color={color} gradientId={gradientId} compact={compact} />
        </Grid>
      </Grid>

      {hasDelta && (
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.75 }}>
          {isUp ? <UpIcon sx={{ fontSize: 15, color: deltaColor }} /> : <DownIcon sx={{ fontSize: 15, color: deltaColor }} />}
          <Typography variant="caption" sx={{ color: deltaColor, fontWeight: 700 }}>
            {isUp ? '+' : ''}{delta}%
          </Typography>
          <Typography variant="caption" sx={{ color: '#9ca3af' }}>vs last cycle</Typography>
        </Stack>
      )}
    </Paper>
  );
};

/**
 * The four trend cards (On-Time %, Late %, Working Hours, Overtime) derived
 * from `data`. Pass `prevData` to show "vs last cycle" deltas. `gridProps`
 * controls how the cards wrap (e.g. {xs:12,sm:6,md:3} for a row, {xs:6} for a
 * narrow sidebar). `idPrefix` keeps the SVG gradient ids unique per instance.
 */
const AttendanceInsights = ({ data, prevData, gridProps = { xs: 12, sm: 6, md: 3 }, spacing = 2, idPrefix = 'ins' }) => {
  const m = useMemo(() => buildAttendanceMetrics(data), [data]);
  const pm = useMemo(() => buildAttendanceMetrics(prevData), [prevData]);
  const work = fmtHoursMinutes(m.workMin);
  const ot = fmtHoursMinutes(m.otMin);
  const lateHours = fmtHoursMinutes(m.lateMin);
  const hasPrev = Boolean(prevData);
  const sidebar = gridProps && gridProps.xs === 6;

  // Two columns when used as a sidebar (gridProps.xs === 6), else a responsive
  // row. CSS grid + gap (no MUI Grid negative margins) so the cards align flush
  // with the summary card stacked above them.
  const gridTemplateColumns = sidebar
    ? 'repeat(2, 1fr)'
    : { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns, gap: spacing }}>
        <InsightCard
          icon={<OnTimeIcon sx={{ fontSize: 17 }} />}
          label="On-Time %"
          value={`${m.onTimePct}%`}
          color="#16a34a"
          series={m.onTimeSeries}
          gradientId={`${idPrefix}-ontime`}
          delta={hasPrev ? pctDelta(m.onTimePct, pm.onTimePct) : null}
          goodWhenUp
        />
        <InsightCard
          icon={<LateIcon sx={{ fontSize: 17 }} />}
          label="Late %"
          value={`${m.latePct}%`}
          color="#ef4444"
          series={m.onTimeSeries.map((p) => ({ v: p.v ? 0 : 1 }))}
          gradientId={`${idPrefix}-late`}
          delta={hasPrev ? pctDelta(m.latePct, pm.latePct) : null}
          goodWhenUp={false}
        />
        <InsightCard
          icon={<HoursIcon sx={{ fontSize: 17 }} />}
          label="Working Hours"
          value={`${work.h}h ${work.m}m`}
          sub="this cycle"
          color="#06b6d4"
          series={m.workSeries}
          gradientId={`${idPrefix}-work`}
          delta={hasPrev ? pctDelta(m.workMin, pm.workMin) : null}
          goodWhenUp
          compact={sidebar}
        />
        <InsightCard
          icon={<OvertimeIcon sx={{ fontSize: 17 }} />}
          label="Overtime Hours"
          value={`${ot.h}h ${ot.m}m`}
          sub="this cycle"
          color="#f59e0b"
          series={m.workSeries}
          gradientId={`${idPrefix}-ot`}
          delta={hasPrev ? pctDelta(m.otMin, pm.otMin) : null}
          goodWhenUp
          compact={sidebar}
        />
        <Box sx={{ gridColumn: sidebar ? '1 / -1' : 'auto' }}>
          <InsightCard
            icon={<LateIcon sx={{ fontSize: 17 }} />}
            label="Late Login Hours"
            value={`${lateHours.h}h ${String(lateHours.m).padStart(2, '0')}m`}
            sub="this cycle"
            color="#ef4444"
            series={m.lateSeries}
            gradientId={`${idPrefix}-late-hours`}
            delta={hasPrev ? pctDelta(m.lateMin, pm.lateMin) : null}
            goodWhenUp={false}
            compact={sidebar}
          />
        </Box>
    </Box>
  );
};

/**
 * Per-day worked-hours bar chart for the cycle — green bars on present/worked
 * days, red on absent/leave, grey otherwise. Fills the sidebar's spare space
 * with a real work-pattern view.
 */
export const DailyHoursChart = ({ data }) => {
  const series = useMemo(() => {
    const days = data?.days || {};
    return Object.entries(days)
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([iso, cell]) => ({
        day: Number(iso.slice(8, 10)),
        hours: cell ? Number((durToMinutes(cell.work_duration) / 60).toFixed(2)) : 0,
        status: cell?.status,
      }));
  }, [data]);

  const barColor = (status) => {
    if (status === 'PRESENT') return '#22c55e';      // light/solid green
    if (status === 'ON_DUTY') return '#06b6d4';      // cyan — matches the tile
    if (status === 'HALF_DAY') return '#a855f7';     // purple — matches the tile
    if (status === 'ABSENT' || status === 'LEAVE') return '#ef4444'; // red
    return '#cbd5e1';                                // grey (holiday / not recorded)
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2, borderRadius: 3, height: '100%',
        border: '1px solid rgba(229,231,235,0.8)',
        background: 'rgba(255,255,255,0.9)',
      }}
    >
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1, color: '#374151' }}>
        Daily working hours
      </Typography>
      <Box sx={{ width: '100%', height: 230 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={series} margin={{ top: 4, right: 4, bottom: 0, left: -24 }}>
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#9ca3af' }} interval={0} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: '#9ca3af' }} axisLine={false} tickLine={false} width={30} allowDecimals={false} />
            <RTooltip formatter={(v) => [`${v} h`, 'Worked']} labelFormatter={(l) => `Day ${l}`} />
            <Bar dataKey="hours" radius={[3, 3, 0, 0]} isAnimationActive={false}>
              {series.map((s, i) => <Cell key={i} fill={barColor(s.status)} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
};

/**
 * Cycle attendance composition as a donut (Present / Absent / Leave / Holiday /
 * Not recorded) with the attendance % in the center. A breakdown you can't get
 * by scanning the calendar.
 */
export const AttendanceBreakdownChart = ({ data }) => {
  const totals = data?.totals || {};
  const pct = data?.attendance_pct ?? 0;
  const slices = [
    { name: 'Present', value: totals.present || 0, color: '#22c55e' },
    { name: 'Absent', value: totals.absent || 0, color: '#dc2626' },
    { name: 'Leave', value: totals.leave || 0, color: '#f87171' },
    { name: 'Holiday', value: totals.holiday || 0, color: '#94a3b8' },
    { name: 'Not recorded', value: totals.not_recorded || 0, color: '#cbd5e1' },
  ].filter((s) => s.value > 0);

  return (
    <Paper
      elevation={0}
      sx={{ p: 2, borderRadius: 3, height: '100%', border: '1px solid rgba(229,231,235,0.8)', background: 'rgba(255,255,255,0.9)' }}
    >
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1, color: '#374151' }}>
        Attendance breakdown
      </Typography>
      <Box sx={{ position: 'relative', width: '100%', height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={slices}
              dataKey="value"
              nameKey="name"
              innerRadius="58%"
              outerRadius="85%"
              paddingAngle={2}
              stroke="none"
              isAnimationActive={false}
            >
              {slices.map((s) => <Cell key={s.name} fill={s.color} />)}
            </Pie>
            <RTooltip formatter={(v, n) => [`${v} day(s)`, n]} />
          </PieChart>
        </ResponsiveContainer>
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1, color: '#111827' }}>{pct}%</Typography>
          <Typography variant="caption" sx={{ color: '#9ca3af' }}>attendance</Typography>
        </Box>
      </Box>
      <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1} justifyContent="center" sx={{ mt: 1 }}>
        {slices.map((s) => (
          <Stack key={s.name} direction="row" spacing={0.5} alignItems="center">
            <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: s.color }} />
            <Typography variant="caption" sx={{ color: '#6b7280' }}>{s.name} ({s.value})</Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

/**
 * Year-level leave + LOP summary (4 tiles): remaining balance, leaves taken,
 * pending-approval count, and total LOP for the year. Fed by the
 * employee year-summary endpoint.
 */
export const YearLeaveSummary = ({ summary, onClose, employeeNo, employeeName, disableLinks = false, leavePath = null }) => {
  const navigate = useNavigate();
  const s = summary || {};
  // `to` makes a tile clickable. Admin (no leavePath): "Leaves taken" → Leave
  // Tracker, "Pending" → Leave Requests, "LOP" → Payslip Logs. Employee
  // (leavePath set): Leave balance / taken / pending all deep-link to the
  // employee leave page; LOP stays display-only (no employee payslip-logs page).
  // The employee (no + name) and year are passed via router state so the target
  // page can focus this person.
  const items = [
    { label: 'Leave balance', value: s.leave_balance ?? '—', sub: 'days left', color: '#16a34a', to: leavePath || null },
    { label: 'Leaves taken', value: s.leaves_taken_days ?? '—', sub: `days · ${s.year || ''}`, color: '#0891b2', to: leavePath || SLUGS.leaveTracker },
    { label: 'Pending', value: s.pending_count ?? '—', sub: 'awaiting approval', color: '#f59e0b', to: leavePath || SLUGS.leaves },
    { label: 'LOP', value: s.lop_total ?? '—', sub: `days · ${s.year || ''}`, color: '#dc2626', to: leavePath ? null : SLUGS.payslipLogs },
  ];

  const go = (to) => {
    if (!to) return;
    if (onClose) onClose();
    navigate(to, { state: { employeeNo, employeeName, year: s.year } });
  };

  return (
    <Paper
      elevation={0}
      sx={{ p: 2, borderRadius: 3, border: '1px solid rgba(229,231,235,0.8)', background: 'rgba(255,255,255,0.9)' }}
    >
      <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1, color: '#374151' }}>
        This year{s.year ? ` · ${s.year}` : ''}
      </Typography>
      <Grid container spacing={1.25}>
        {items.map((it) => {
          const clickable = Boolean(it.to) && !disableLinks;
          return (
            <Grid item xs={6} key={it.label}>
              <Box
                onClick={clickable ? () => go(it.to) : undefined}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(it.to); } } : undefined}
                sx={{
                  position: 'relative', p: 1.25, borderRadius: 2,
                  bgcolor: alpha(it.color, 0.1), height: '100%',
                  cursor: clickable ? 'pointer' : 'default',
                  transition: 'background-color .15s, box-shadow .15s',
                  ...(clickable && {
                    '&:hover': { bgcolor: alpha(it.color, 0.18), boxShadow: `0 1px 6px ${alpha(it.color, 0.35)}` },
                  }),
                }}
              >
                <Typography variant="h6" fontWeight={800} sx={{ color: it.color, lineHeight: 1.1 }}>{it.value}</Typography>
                <Typography variant="caption" sx={{ color: '#374151', fontWeight: 700, display: 'block', lineHeight: 1.2 }}>{it.label}</Typography>
                <Typography variant="caption" sx={{ color: '#9ca3af', lineHeight: 1.2 }}>{it.sub}</Typography>
                {clickable && (
                  <ChevronRightIcon sx={{ position: 'absolute', top: 6, right: 4, fontSize: 16, color: alpha(it.color, 0.7) }} />
                )}
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

export default AttendanceInsights;
